/**
 * Pipeline Constants — Heart of Science v2
 * Single source of truth for pipeline stages and transitions
 * S9-05: MAPPED stage removed per CEO decision 2026-04-07 (Sprint 10 as separate Ecosystem Map module)
 */

// Simple array of stage codes (lowercase-uppercase as per existing data)
export const PIPELINE_STAGE_CODES = [
  'INBOX',
  'LONG_LIST',
  'PICKED_FOR_REVIEW',
  'IN_REVIEW',
  'TO_PRESENT',
  'EXTRA_MILE',
  'VB_PROCESS',
  'FUNDED',
  'EXITED',
  'PARKED',
  'GRAVEYARD',
] as const;

export type PipelineStage = typeof PIPELINE_STAGE_CODES[number];

/**
 * Allowed stage transitions (mirrors both API and frontend validation)
 * Used by both backend validation and frontend drag-and-drop
 * Early stages allow backward moves (classification corrections) per S9 hotfix
 */
export const ALLOWED_TRANSITIONS: Record<PipelineStage, PipelineStage[]> = {
  INBOX: ['LONG_LIST', 'PARKED', 'GRAVEYARD'],
  LONG_LIST: ['INBOX', 'PICKED_FOR_REVIEW', 'PARKED', 'GRAVEYARD'],
  PICKED_FOR_REVIEW: ['INBOX', 'LONG_LIST', 'IN_REVIEW', 'PARKED', 'GRAVEYARD'],
  IN_REVIEW: ['TO_PRESENT', 'PICKED_FOR_REVIEW', 'PARKED', 'GRAVEYARD'],
  TO_PRESENT: ['EXTRA_MILE', 'IN_REVIEW', 'PARKED', 'GRAVEYARD'],
  EXTRA_MILE: ['VB_PROCESS', 'TO_PRESENT', 'PARKED', 'GRAVEYARD'],
  VB_PROCESS: ['FUNDED', 'EXITED', 'PARKED', 'GRAVEYARD'],
  FUNDED: ['EXITED', 'PARKED'],
  EXITED: [],
  PARKED: ['INBOX', 'LONG_LIST', 'PICKED_FOR_REVIEW', 'IN_REVIEW', 'TO_PRESENT', 'EXTRA_MILE', 'VB_PROCESS'],
  GRAVEYARD: ['LONG_LIST'],
};

/**
 * S9-07: Pipeline stage rank for detecting backward transitions
 * Used to determine if a transition moves back in the pipeline
 */
export const STAGE_RANK: Record<PipelineStage, number> = {
  INBOX: 0,
  LONG_LIST: 1,
  PICKED_FOR_REVIEW: 2,
  IN_REVIEW: 3,
  TO_PRESENT: 4,
  EXTRA_MILE: 5,
  VB_PROCESS: 6,
  FUNDED: 7,
  EXITED: 8,
  PARKED: 9, // Recovery stage (allows moving back into main flow)
  GRAVEYARD: 10, // Archive stage (very restricted recovery)
};

/**
 * S9-07: Check if a transition is backward (return to earlier stage in pipeline)
 * PARKED and GRAVEYARD are recovery stages; transitions FROM them can be forward or recovery
 */
export function isBackwardTransition(fromStage: PipelineStage, toStage: PipelineStage): boolean {
  const fromRank = STAGE_RANK[fromStage] ?? 0;
  const toRank = STAGE_RANK[toStage] ?? 0;
  // Backward = going to a lower-ranked stage
  // Exception: PARKED and GRAVEYARD are recovery stages, so leaving them is not "backward"
  if (fromStage === 'PARKED' || fromStage === 'GRAVEYARD') {
    return false; // Recovery moves are not considered backward
  }
  return toRank < fromRank;
}
